import { PcbNodeTypes, asInferredNode, asInferredNodes, pcbLayoutRootNodeId, } from "@buildwithflux/models";
import find from "lodash/find";
import findLast from "lodash/findLast";
import isEmpty from "lodash/isEmpty";
import pick from "lodash/pick";
import { MissingChildUidError } from "../flux_error";
import { PcbLayoutNodeDataIntegrityError } from "./error";
import { PcbNodeHelper } from "./pcbNodeHelper";
/**
 * Get all nodes of a specific type from a collection of pcb nodes
 */
export function getNodesOfType(pcbNodes, nodeType, cache = new Map()) {
    let nodeIds = cache.get(nodeType);
    if (!nodeIds) {
        nodeIds = Object.keys(pcbNodes).filter((nodeId) => {
            const node = pcbNodes[nodeId];
            if (!node) {
                PcbNodeHelper.onError(new PcbLayoutNodeDataIntegrityError(`node is undefined in getNodesOfType. node type: ${nodeType}`));
                return false;
            }
            return node.type === nodeType;
        });
        cache.set(nodeType, nodeIds);
    }
    return nodeIds.map((nodeId) => asInferredNode(pcbNodes[nodeId]));
}
/**
 * QUESTION: do we still need this at all? or can we rely on filterOrphanedNodes
 * at load time?
 *
 * @deprecated avoid this expensive function if possible
 */
export function isNodeInTree(pcbNodes, nodeUid, cache = { entry: undefined }) {
    // Many documents have orphaned nodes from old dead (or maybe even existing!) code.
    // We should exclude those nodes from all code; this function makes that possible,
    // but we need to implement this everywhere. Currently it's ad-hoc.
    // See eg useLayoutShape.tsx
    return getIsInTreeIndex(pcbNodes, cache)[nodeUid] ?? false;
}
function getIsInTreeIndex(pcbNodes, cache = { entry: undefined }) {
    if (!cache.entry) {
        const index = {};
        const addNodeToIndex = (uid) => {
            if (uid in index) {
                return;
            }
            const node = pcbNodes[uid];
            if (typeof node === "undefined") {
                index[uid] = false;
            }
            else if (isTopLevelNode(node)) {
                index[uid] = true;
            }
            else {
                addNodeToIndex(node.parentUid);
                index[uid] = index[node.parentUid] ?? false;
            }
        };
        for (const uid in pcbNodes) {
            addNodeToIndex(uid);
        }
        cache.entry = index;
    }
    return cache.entry;
}
/**
 * Check if a node is a top-level one.
 *
 * Any node thats a direct child of the "ROOT" node is a top-level node
 *
 * @param node - node we are checking
 */
export function isTopLevelNode(node) {
    return node.parentUid === pcbLayoutRootNodeId;
}
/**
 * Similar check as `isTopLevelNode`, but also check if node.type is matching
 *
 * @param node - node we are checking
 * @param type - node type we are looking for
 */
export function isTopLevelNodeOfType(node, type) {
    if (!node || node.type !== type)
        return false;
    return isTopLevelNode(node);
}
/**
 * The default layout/footprint (with "default" string in uid) is only
 * created when back-filling. See createDefaultLayoutNode and
 * createRootFootprintNode
 */
export function getFirstTopLevelFootprint(pcbNodes) {
    return getTopLevelFootprints(pcbNodes)[0];
}
/**
 * The default layout/footprint (with "default" string in uid) is only
 * created when back-filling. See createDefaultLayoutNode and
 * createRootFootprintNode
 */
export function getFirstTopLevelLayout(pcbNodes) {
    return getTopLevelLayouts(pcbNodes)[0];
}
export function getTopLevelFootprints(pcbNodes) {
    return asInferredNodes(getCandidateTopLevelNodes(pcbNodes).filter((node) => isTopLevelNodeOfType(pcbNodes[node.uid], PcbNodeTypes.footprint)));
}
export function getFirstTopLevelModel(pcbNodes) {
    return getTopLevelModels(pcbNodes)[0];
}
export function getTopLevelModels(pcbNodes) {
    return asInferredNodes(getCandidateTopLevelNodes(pcbNodes).filter((node) => isTopLevelNodeOfType(pcbNodes[node.uid], PcbNodeTypes.model)));
}
// The order of the returned uids matters!
export function getAncestorUids(pcbNodes, node, includeRoot = false, cache = {}) {
    const cacheKey = `${node.uid}:${includeRoot}`;
    const indexedResult = cache[cacheKey];
    if (indexedResult) {
        return indexedResult;
    }
    let ancestorUids = [];
    const parentNode = pcbNodes[node?.parentUid];
    if (parentNode) {
        ancestorUids = [node.parentUid, ...getAncestorUids(pcbNodes, parentNode, includeRoot, cache)];
    }
    else if (includeRoot && isTopLevelNode(node)) {
        // Test equality above so we only include root if the top node is actually root -- eg
        // don't include root if it's an orphaned branch or not-yet-added; that could get messy.
        ancestorUids.push(pcbLayoutRootNodeId);
    }
    cache[cacheKey] = ancestorUids;
    return ancestorUids;
}
/*
 * Iterate over all ancestors of a node, starting from the parent node and going up to the root.
 */
export function* iterateAncestors(node, nodes) {
    for (let ancestor = nodes[node.parentUid]; ancestor; ancestor = nodes[ancestor.parentUid]) {
        yield ancestor;
    }
}
/**
 * Given a node uid, return uid of its ancestor of a given type.
 *
 * Use `topDown` parameter to decide which ancestor you would like this function to return.
 *
 * @param pcbNodes - node map to travers & find the ancestor
 * @param nodeUid - node uid you are querying
 * @param nodeType - type of the ancestor node you are targeting
 * @param topDown - search direction: when its false, this function returns the nearest found ancestor;
 * when its true, this function returns the furthest found ancestor. By default this is set to false
 */
export function getAncestorUidByType(pcbNodes, nodeUid, nodeType, topDown = false, cache = {}) {
    if (nodeType === PcbNodeTypes.root) {
        throw new Error("If you really want the root node uid, simply use `pcbLayoutRootNodeId` directly.");
    }
    const node = pcbNodes[nodeUid];
    if (!node) {
        throw new Error(`Node object with ${nodeUid} does not exist.`);
    }
    const cacheKey = `${nodeUid}:${nodeType}:${topDown}`;
    const indexedResult = cache[cacheKey];
    if (indexedResult) {
        return indexedResult;
    }
    const ancestorUids = getAncestorUids(pcbNodes, node);
    const _find = topDown ? findLast : find;
    const ancestorUid = _find(ancestorUids, (ancestorUid) => pcbNodes[ancestorUid]?.type === nodeType);
    if (ancestorUid)
        cache[cacheKey] = ancestorUid;
    return ancestorUid;
}
/**
 * Check if a node is child of a footprint node
 *
 * NOTE that a footprint node can be either a top-level one OR one under a sublayout, sometimes
 * we only want to check if the node is a child of a sublayout's footprint
 *
 * TODO: Remove checkSubLayoutOnly flag and add a wrapper function isChildOfSublayoutFootprint to handle that logic
 *
 * @param pcbNodes - all nodes
 * @param childNodeUid - uid of the node we are checking
 * @param checkSubLayoutOnly - if set, we only return true if the passed in node is a child of a sub-layout footprint
 */
export function isDescendantOfFootprint(pcbNodes, childNodeUid) {
    const ancestorFootprintUid = getAncestorUidByType(pcbNodes, childNodeUid, PcbNodeTypes.footprint);
    return Boolean(ancestorFootprintUid);
}
/**
 * Check if a node is child of an element node
 *
 * @param pcbNodes - all nodes
 * @param childNodeUid - uid of the node we are checking
 */
export function isDescendantOfElement(pcbNodes, childNodeUid) {
    const ancestorElementUid = getAncestorUidByType(pcbNodes, childNodeUid, PcbNodeTypes.element);
    return Boolean(ancestorElementUid);
}
export function isDescendantOfLayout(pcbNodes, nodeUid) {
    const ancestorUids = getAncestorUids(pcbNodes, pcbNodes[nodeUid]);
    return ancestorUids.some((uid) => pcbNodes[uid].type === PcbNodeTypes.layout);
}
export function getTopLevelLayouts(pcbNodes) {
    return asInferredNodes(getCandidateTopLevelNodes(pcbNodes).filter((node) => isTopLevelNodeOfType(pcbNodes[node.uid], PcbNodeTypes.layout)));
}
// We have test coverage for `getTopLevelLayouts` and `getTopLevelFootprints`
export function getFirstTopLevelContainerNode(pcbNodes) {
    const layouts = getTopLevelLayouts(pcbNodes);
    if (layouts.length)
        return layouts[0];
    return getTopLevelFootprints(pcbNodes)[0];
}
export function hasTopLevelContainerNode(nodes) {
    return !!nodes.find((node) => isTopLevelNodeOfType(node, PcbNodeTypes.layout) || isTopLevelNodeOfType(node, PcbNodeTypes.footprint));
}
/**
 * Given a node (uid), find its top level layout node and return.
 *
 * Returns undefined if not found
 */
export function getTopLevelLayoutOfNode(pcbNodes, nodeUid) {
    const topLevelLayoutNodeUid = getAncestorUidByType(pcbNodes, nodeUid, PcbNodeTypes.layout, true);
    if (topLevelLayoutNodeUid) {
        return asInferredNode(pcbNodes[topLevelLayoutNodeUid]);
    }
    return undefined;
}
/**
 * Given a tree branch's root node, returns all nodes of a type in that subtree.
 *
 * Note that this also returns the tree branch's root node if it's node type also matches
 *
 * Use {@param ignoreUids} to ignore uids even they match the conditions
 * Use {@param excludeNestedMatches} if true, return only matches that aren't inside another match
 */
export function getDescendantsOfType(pcbNodes, treeBranchRootNode, nodeType, ignoreUids = [], includeNestedMatches = true) {
    const result = [];
    if (treeBranchRootNode?.type === nodeType) {
        result.push(asInferredNode(treeBranchRootNode));
        if (!includeNestedMatches) {
            return result;
        }
    }
    const treeBranchRootChildUids = pcbNodes[treeBranchRootNode.uid]?.childUids ?? [];
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    for (const childNodeUid of treeBranchRootChildUids) {
        if (ignoreUids.includes(childNodeUid)) {
            continue;
        }
        const node = pcbNodes[childNodeUid];
        if (!node) {
            // Skip non-existent nodes because pcbNodes isn't always updated
            // immediately when nodes are deleted. See equivalent comment in
            // PcbLayoutEngine:bakePcbLayoutNode()
            continue;
        }
        result.push(...getDescendantsOfType(pcbNodes, node, nodeType, ignoreUids, includeNestedMatches));
    }
    return result;
}
export function getTopLevelLayoutNonElementDescendantsPads(pcbNodes, rootLayoutUid, rootLayoutElements) {
    const rootLayout = pcbNodes[rootLayoutUid];
    // QUESTION: should rename var to pads??
    const layoutElements = getDescendantsOfType(pcbNodes, rootLayout, PcbNodeTypes.pad, rootLayoutElements.map((node) => node.uid));
    return layoutElements.filter((node) => !node.terminalId);
}
export function getTopLevelPcbLayoutNodeUids(pcbNodes) {
    return getCandidateTopLevelNodes(pcbNodes)
        .filter((node) => isTopLevelNode(node))
        .map((node) => node.uid);
}
export function getChildByName(pcbNodes, parentUid, name, logger) {
    // TODO: Combine this with the one in `getNodeOfType` and move this to domain level
    const node = pcbNodes[parentUid];
    if (!node && !isEmpty(pcbNodes)) {
        const error = new PcbLayoutNodeDataIntegrityError(`Cant find node data for "${parentUid}" in getChildByName`);
        throw error;
    }
    // NOTE: We play safe here to fallback childUids to empty, but want to know
    // that childUids is missing, so that we can investigate and take actions from here
    if (node && !node.childUids) {
        if ("captureError" in logger) {
            logger.captureError(new MissingChildUidError(`node ${parentUid} with type ${node.type} in getChildByName`));
        }
        else {
            logger.error(`node ${parentUid} with type ${node.type} in getChildByName`);
        }
    }
    const children = Object.values(pick(pcbNodes, node?.childUids || []));
    return children.find((n) => n?.name.toLowerCase() === name.toLowerCase());
}
/**
 * Get descendants of an array of given tree branch roots. The descendants will be ordered
 * in PreOrder manner.
 *
 * Please note that this function will NOT return the `treeBranchRoots`, but only return their
 * descendants.
 *
 * The returned result will be [...descendants1, ...descendants2, ...]
 *
 *
 * @param {PcbNodesMap<AnyPcbNode>} pcbNodes - the tree nodes that `treeBranchRoots` and their descendants belong to
 * @param {AnyPcbNode[]} treeBranchRoots - list of tree branch roots we are intrested in
 */
export function getDescendantsOfTreeBranchRoots(pcbNodes, treeBranchRoots) {
    let result = [];
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    treeBranchRoots.forEach((node) => {
        const childUids = pcbNodes[node.uid]?.childUids ?? [];
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        childUids.forEach((childUid) => {
            const childNode = pcbNodes[childUid];
            if (childNode) {
                /**
                 * NOTE: this order is important to make sure the traversal is in Preorder:
                 * we always push the `childNode` first before diving into the next level and push its descendants
                 */
                result.push(childNode);
                result = result.concat(getDescendantsOfTreeBranchRoots(pcbNodes, [childNode]));
            }
        });
    });
    return result;
}
export function getChildrenOfType(pcbNodes, nodeUid, type, logger) {
    // TODO: Combine this with the one in `getNodeOfType`
    const node = pcbNodes[nodeUid];
    if (!node) {
        if (!isEmpty(pcbNodes)) {
            const error = new PcbLayoutNodeDataIntegrityError(`Cant find node data for "${nodeUid}" in getChildrenOfType`);
            if (logger) {
                if ("captureError" in logger) {
                    logger.captureError(error);
                }
            }
            throw error;
        }
        return [];
    }
    // NOTE: We play safe here to fallback childUids to empty, but want to know
    // that childUids is missing, so that we can investigate and take actions from here
    if (!node.childUids) {
        if (logger) {
            if ("captureError" in logger) {
                logger.captureError(new MissingChildUidError(`node ${nodeUid} with type ${node.type} in getChildrenOfType`));
            }
            else {
                logger.error(`node ${nodeUid} with type ${node.type} in getChildrenOfType`);
            }
        }
    }
    const children = asInferredNodes(Object.values(pick(pcbNodes, node.childUids || [])));
    return children.filter((n) => n.type === type);
}
/**
 * Iterates all descendants of a node in DFS manner
 */
export function* iterateDescendants(node, pcbNodes) {
    if (!node.childUids) {
        return;
    }
    for (const childUid of node.childUids) {
        const childNode = pcbNodes[childUid];
        if (!childNode) {
            continue;
        }
        yield childNode;
        yield* iterateDescendants(childNode, pcbNodes);
    }
}
/**
 * Iterates a node and all its descendants in DFS manner, starting from the node itself
 */
export function* iterateNodeAndDescendants(node, pcbNodes) {
    yield node;
    yield* iterateDescendants(node, pcbNodes);
}
/**
 * @deprecated Use iterateDescendants() instead for better performance and memory usage. The iterator avoids internals array allocation/concatenation and allows early termination from calling code.
 */
export function getDescendants(pcbNodes, node) {
    const childUids = pcbNodes[node.uid]?.childUids ?? [];
    return childUids.flatMap((childNodeUid) => {
        const childNode = pcbNodes?.[childNodeUid];
        return childNode ? [childNode, ...getDescendants(pcbNodes, childNode)] : [];
    });
}
/**
 * Recursively get all element nodes under a layout, excluding elements that contain sub-layouts.
 * This function traverses the layout tree and returns only leaf element nodes.
 *
 * @param pcbNodes - all nodes
 * @param layoutNodeUid - UID of the layout node to start from
 * @returns array of element nodes that don't contain sub-layouts
 */
function getAllLeafElementNodesUnderLayout(pcbNodes, layoutNodeUid) {
    const allElementNodes = [];
    const layoutNode = pcbNodes[layoutNodeUid];
    if (!layoutNode) {
        return allElementNodes;
    }
    // Use includeNestedMatches=false to only get the immediate element descendants of this layout,
    // NOT elements nested inside sub-layouts of other elements. Without this, an element containing a
    // sub-layout (e.g. a sub-module) would cause its inner elements to appear both here AND again when
    // the recursive call below processes that sub-layout, resulting in duplicate entries that cause
    // downstream failures (e.g. "Component 'X' is defined more than once" in the ALIFF exporter).
    const thisLayoutElementNodes = getDescendantsOfType(pcbNodes, layoutNode, PcbNodeTypes.element, [], false);
    for (const elementNode of thisLayoutElementNodes) {
        const subLayoutNodes = getChildrenOfType(pcbNodes, elementNode.uid, PcbNodeTypes.layout);
        // If this node has sub-layouts, we need to dive into those sub-layouts to find leaf element nodes
        // Otherwise, this is a leaf element node and we can add it to the result
        if (subLayoutNodes.length) {
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            subLayoutNodes.forEach((subLayoutNode) => {
                allElementNodes.push(...getAllLeafElementNodesUnderLayout(pcbNodes, subLayoutNode.uid));
            });
        }
        else {
            allElementNodes.push(elementNode);
        }
    }
    return allElementNodes;
}
/**
 * Recursively get all element nodes under layout(s), excluding elements that contain sub-layouts.
 * This function traverses the layout tree and returns only leaf element nodes.
 *
 * @param pcbNodes - all nodes
 * @param layoutNodeUid - optional UID of a specific layout node to start from. If not provided, all top-level layouts are used
 * @returns array of element nodes that don't contain sub-layouts
 */
export function getAllLeafElementNodes(pcbNodes, layoutNodeUid) {
    if (layoutNodeUid) {
        return getAllLeafElementNodesUnderLayout(pcbNodes, layoutNodeUid);
    }
    const allElementNodes = [];
    const topLevelLayouts = getTopLevelLayouts(pcbNodes);
    for (const layout of topLevelLayouts) {
        allElementNodes.push(...getAllLeafElementNodesUnderLayout(pcbNodes, layout.uid));
    }
    return allElementNodes;
}
/**
 * Use the root node when available for performance. It should always be
 * available in the current doc but not always in test code or in part data.
 */
export function getCandidateTopLevelNodes(pcbNodes) {
    const rootNode = pcbNodes[pcbLayoutRootNodeId];
    const nodesArr = Object.values(pcbNodes);
    if (rootNode && rootNode.childUids && nodesArr.length > 1) {
        return rootNode.childUids
            .map((uid) => pcbNodes[uid])
            .filter((node) => node !== undefined);
    }
    return nodesArr;
}
//# sourceMappingURL=traversal.js.map